import dayjs, { Dayjs } from "dayjs";
import type DecimalType from "decimal.js";

import { Allergen, CatLifeStage } from "@/primary-models";

import {
  ADULT_CAT_AGE_YEARS,
  BODY_TYPE_CALORIE_MULTIPLIER,
  BREEDS,
  CALORIE_THRESHOLD,
  GROWTH_CURVE,
  LIFESTYLE_CALORIE_MULTIPLIER,
  MEAL_PLAN_CALORIES,
  RETAIL_PLAN_PORTION,
  SENIOR_CAT_AGE_YEARS,
  UNIVERSAL_ALLERGENS,
  WEEKS_PER_MONTH,
} from "./cat-calculations.constants";
import {
  BodyType,
  Breed,
  Gender,
  ICatAge,
  IFraction,
  IMealPlan,
  IRetailTrayPortion,
  LifeStyle,
  Portion,
  PouchSize,
  TDecimalRounding,
} from "./cat-calculations.types";

export const calculateCatWeight = (
  breed: Breed,
  age: ICatAge,
  gender: Gender,
): number => {
  let multiplier = 100;
  const breedDetails = BREEDS.find(
    (mappedBreed) => mappedBreed.value === breed,
  );
  if (!breed || !age || !gender || !breedDetails) return 0;

  if (age.type === "kitten") {
    const weekSelector = Number(
      (((age.months || 0) * 4.33 + (age.weeks || 0)) / 2).toFixed(0),
    );
    multiplier = GROWTH_CURVE[weekSelector];
  }

  return Number(
    (
      ((gender && gender === "BOY"
        ? breedDetails.maleAdultWeight
        : breedDetails.femaleAdultWeight) *
        multiplier) /
      100
    ).toFixed(1),
  );
};

export const range = (
  start: number,
  stop: number,
) => Array.from({ length: stop - start + 1 }, (value, index) => start + index);

export const isKitten = (age: ICatAge) => {
  if (age.type && [ "adult", "senior" ].includes(age.type)) return false;
  if (age.years && age.years >= 1) return false;
  if (age.months && age.months >= 12) return false;
  if (age.weeks && age.weeks >= 52) return false;

  return true;
};

export const calculateSuggestedCaloriesForKitten = (
  weight: number,
  ageInWeeks: number,
): number => {

  const matureBodyWeight =
    weight /
    ((-0.0474 * (ageInWeeks * ageInWeeks) + (4.47 * ageInWeeks - 5.5784)) /
      100);

  if (range(1, 14).includes(ageInWeeks)) {
    return Math.floor(
      100 *
      weight ** 0.67 *
      6.7 *
      (2.718 ** (-0.189 * (weight / matureBodyWeight)) - 0.66),
    );
  }
  if (range(15, 40).includes(ageInWeeks)) return Math.floor(1.75 * 50 * matureBodyWeight);
  if (range(41, 48).includes(ageInWeeks)) return Math.floor(1.5 * 50 * matureBodyWeight);
  return Math.floor(1.25 * 50 * matureBodyWeight);
};

export const calculateSuggestedCaloriesForKittenByAgeObject = (
  weight: number,
  age: ICatAge,
): number => {
  const ageInWeeks = Math.round(
    (Number(age.weeks) || 0) + (Number(age.months) || 0) * WEEKS_PER_MONTH,
  );

  return calculateSuggestedCaloriesForKitten(weight, ageInWeeks);

};

export const calculateSuggestedCaloriesForGrownCat = (
  weight: number,
  bodyType: BodyType,
  lifestyle: LifeStyle,
): number => {
  const idealBodyWeight =
    (weight / BODY_TYPE_CALORIE_MULTIPLIER[bodyType]) *
    LIFESTYLE_CALORIE_MULTIPLIER[lifestyle];
  return Math.round(idealBodyWeight * 50);
};

export const calculateSuggestedCaloriesFromBirthday = (
  weight: number,
  bodyType: BodyType,
  lifestyle: LifeStyle,
  birthday: Dayjs,
): number => {
  if (dayjs().diff(birthday, "years") < 1) {
    const ageInWeeks = dayjs().diff(birthday, "weeks");
    return calculateSuggestedCaloriesForKitten(weight, ageInWeeks);
  }
  return calculateSuggestedCaloriesForGrownCat(weight, bodyType, lifestyle);
};

export const calculateSuggestedCalories = (
  weight: number,
  bodyType: BodyType,
  lifestyle: LifeStyle,
  age: ICatAge,
): number => {
  if (isKitten(age)) {
    return calculateSuggestedCaloriesForKittenByAgeObject(weight, age);
  }
  return calculateSuggestedCaloriesForGrownCat(weight, bodyType, lifestyle);
};

export const calculateTraySize = (caloriesNeeded: number): IMealPlan => {
  const trays = Object.entries(MEAL_PLAN_CALORIES);

  for (const mealPlan of trays) {
    if (mealPlan) {
      const [ size, calories ]: [string, number] = mealPlan;
      if (caloriesNeeded <= calories + CALORIE_THRESHOLD) {
        return {
          size: size as PouchSize,
          calories,
        };
      }
    }
  }
  const [ size, calories ]: [string, number] = trays[trays.length - 1];

  return {
    size: size as PouchSize,
    calories,
  };
};

export const calculateRetailTrayServings = (
  caloriesNeeded: number,
): IRetailTrayPortion => {
  const trays = Object.entries(RETAIL_PLAN_PORTION);

  for (const mealPlan of trays) {
    if (mealPlan) {
      const [ calories, portion ] = mealPlan;
      if (caloriesNeeded <= +calories + CALORIE_THRESHOLD) {
        return {
          portion: portion as Portion,
          calories: +calories,
        };
      }
    }
  }
  const [ calories, portion ]: [string, number] = trays[trays.length - 1];

  return {
    portion: portion as Portion,
    calories: +calories,
  };
};

export const calculateSuggestedCatTraySize = (
  weight: number,
  bodyType: BodyType,
  lifestyle: LifeStyle,
  age: ICatAge,
): IMealPlan => {
  const suggestedCalories = calculateSuggestedCalories(
    weight,
    bodyType,
    lifestyle,
    age,
  );
  return calculateTraySize(suggestedCalories);
};

const getGreatestCommonDivisor = (a: number, b: number): number => (a % b ? getGreatestCommonDivisor(b, a % b) : b);

export const makeFractionFromDecimal = (
  decimal: number,
  denominator: number,
  rounding: TDecimalRounding = "UP",
): IFraction => {
  const factor: number = 100 / denominator;
  const numeratorTotal = (decimal * 100) / factor;
  const wholeNumber = Math.floor(numeratorTotal / denominator);
  let decimalPlaces = numeratorTotal / denominator - wholeNumber;

  if (rounding === "UP") {
    decimalPlaces = Math.ceil(decimalPlaces / (factor / 100)) * (factor / 100);
  } else {
    decimalPlaces = Math.floor(decimalPlaces / (factor / 100)) * (factor / 100);
  }
  const numerator = (decimalPlaces * 100) / factor;

  const gcd = getGreatestCommonDivisor(numerator, denominator);
  const numeratorAdjustedForLowestCommonDenominator = numerator / gcd;
  const denominatorAdjustedForLowestCommonDenominator = denominator / gcd;

  return {
    wholeNumber: wholeNumber > 0 ? wholeNumber : undefined,
    fraction:
      numeratorAdjustedForLowestCommonDenominator > 0
        ? {
          numerator: numeratorAdjustedForLowestCommonDenominator,
          denominator: denominatorAdjustedForLowestCommonDenominator,
        }
        : undefined,
  };
};

export const roundToNearest50 = (caloricNeed: number): number => {
  const maxCalories = 400;
  const calorieRanges = [
    { max: 175, calories: 150 },
    { max: 225, calories: 200 },
    { max: 275, calories: 250 },
    { max: 325, calories: 300 },
    { max: 375, calories: 350 },
    { max: Infinity, calories: maxCalories },
  ];

  const calorieRange = calorieRanges.find((r) => caloricNeed < r.max);
  return calorieRange?.calories ?? maxCalories;
};

export function getCatLifeStage(birthday: dayjs.ConfigType): CatLifeStage {

  if (!birthday) {
    // If the cat has no birthday, we assume it's an adult.
    return "ADULT";
  }

  const age = dayjs().diff(birthday, "year");

  if (age < 0) {
    return "ADULT";
  }

  if (age < ADULT_CAT_AGE_YEARS[0]) {
    return "KITTEN";
  }

  if (age < SENIOR_CAT_AGE_YEARS[0]) {
    return "ADULT";
  }

  return "SENIOR";
}

export const breedHairMap = new Map(
  BREEDS.map((b) => [ b.value, b.isLongHaired ] as const),
);

export const isLongHairedBreed = (breed: Breed) => breedHairMap.get(breed) ?? false;

const DAYS_PER_MONTH = 28;

export const getCompetitorMonthlyPriceDiff =
(katkinPrice: number, competitorPrice: number) => +(
  katkinPrice * DAYS_PER_MONTH - (competitorPrice || 0) * DAYS_PER_MONTH).toFixed();

export const isCatFullyAllergic = (allergens?: Allergen[]): boolean => {
  if (!allergens || allergens.length < UNIVERSAL_ALLERGENS.length) { return false; }

  const allergenSet = new Set(allergens);
  return UNIVERSAL_ALLERGENS.every((allergen) => allergenSet.has(allergen));
};

/** Converts a number of grams to kilograms with a maximum of 1 decimal place.
 * So, for example, 1234 grams would be converted to 1.2 kilograms.
 * @return The converted number or NaN if the input was invalid. */
export function gramsToKgs(grams: number | string | DecimalType): number {
  return Math.round(Number(grams) / 100) / 10;
}

/** Converts a number of kilograms to grams with a maximum of 0 decimal places.
 * So, for example, 1.2345 kilograms would be converted to 1235 grams.
 * @return The converted number or NaN if the input was invalid. */
export function kgsToGrams(kgs: number | string | DecimalType): number {
  return Math.round(Number(kgs) * 1000);
}

export function getAgeAtSignup(
  cat: {
    catLifeStage?: "kitten" | "adult" | "senior";
    catAgeMonths?: number;
    catAgeWeeks?: number;
    catAgeYears?: number;
  },
): {
    years?: number | null;
    months?: number | null;
    weeks?: number | null;
  } | undefined {
  if (cat.catLifeStage === "kitten") {
    if (cat.catAgeMonths || cat.catAgeWeeks) {
      return {
        months: cat.catAgeMonths ?? null,
        weeks: cat.catAgeWeeks ?? null,
      };
    }
    return undefined;
  }
  if (cat.catAgeYears) {
    return {
      years: cat.catAgeYears,
    };
  }
  return undefined;

}
